import {
  ColumnSizingState,
  PaginationState,
  SortingState,
  GroupingState,
  RowPinningState,
} from '@tanstack/react-table';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

const TABLE_COLUMN_SETTINGS_KEY = 'TABLE_COLUMN_SETTINGS';

export interface TableState {
  loading: boolean;
  totalRecords: number;
  pagination: PaginationState;
  sorting: SortingState;
  selectedRow: string | undefined;
  grouping: GroupingState;
  rowPinning: RowPinningState;
  expandLoading: Record<string, boolean>;
  expandCompareKey?: string;
  dataPropertyForExpandComparison: string;
  loadingRowCountKey?: string;
  highlightedField?: string | null;
  highlightedValue?: string | null;
  columnOrderMap: Record<string, string[]>;
  columnSizingMap: Record<string, ColumnSizingState>;
}

interface TableStateActions {
  setLoading: (id: string, x: boolean) => void;
  setTotalRecords: (id: string, x: number) => void;
  setPagination: (id: string, x: Partial<PaginationState>) => void;
  setGrouping: (id: string, x: GroupingState) => void;
  setSorting: (id: string, x: SortingState) => void;
  setRowPinning: (id: string, x: RowPinningState) => void;
  selectRow: (id: string, rowId: string | undefined) => void;
  register: (id: string, initState: Partial<TableState>) => void;
  unregister: (id: string) => void;
  setExpandLoading: (id: string, expandLoading: Record<string, boolean>) => void;
  setExpandCompareKey: (id: string, expandCompareKey: string) => void;
  setLoadingRowCountKey: (id: string, loadingRowCountKey: string) => void;
  setDataPropertyForExpandComparison: (id: string, dataPropertyForExpandComparison: string) => void;
  setHighlightedFieldAndValue: (id: string, field: string | null, value: string | null) => void;
  setColumnOrder: (id: string, columnIdKey: string, columnOrder: string[]) => void;
  setColumnSizing: (id: string, columnIdKey: string, columnSizing: ColumnSizingState) => void;
  resetColumnState: (id: string, columnIdKey: string) => void;
}

interface TableStateById extends TableStateActions {
  state: Record<string, TableState>;
  persistedColumnState: Record<
    string,
    { columnOrderMap: Record<string, string[]>; columnSizingMap: Record<string, ColumnSizingState> }
  >;
}

const initTableState: TableState = {
  loading: false,
  totalRecords: 0,
  pagination: {
    pageIndex: 0,
    pageSize: 10,
  },
  sorting: [],
  selectedRow: undefined,
  grouping: [],
  rowPinning: {
    top: [],
    bottom: [],
  },
  expandLoading: {},
  dataPropertyForExpandComparison: 'hcoName',
  highlightedField: null,
  highlightedValue: null,
  columnOrderMap: {},
  columnSizingMap: {},
};

const useTableStore = create<TableStateById>()(
  persist(
    immer((set, get) => {
      const updateTableField = (id: string, updates: Partial<TableState>) => {
        set(draft => {
          if (!draft.state[id]) return;
          Object.assign(draft.state[id], updates);
        });
      };

      const ensurePersistedColumnState = (draft: TableStateById, id: string) => {
        if (!draft.persistedColumnState[id]) {
          draft.persistedColumnState[id] = {
            columnOrderMap: { ...draft.state[id].columnOrderMap },
            columnSizingMap: { ...draft.state[id].columnSizingMap },
          };
        }
      };

      return {
        state: {},
        persistedColumnState: {},
        setLoading: (id, loading) => updateTableField(id, { loading }),
        setTotalRecords: (id, totalRecords) => updateTableField(id, { totalRecords }),
        setPagination: (id, pagination) => {
          set(draft => {
            if (!draft.state[id]) return;
            Object.assign(draft.state[id].pagination, pagination);
          });
        },
        setSorting: (id, sorting) => updateTableField(id, { sorting }),
        selectRow: (id, selectedRow) => updateTableField(id, { selectedRow }),
        setGrouping: (id, grouping) => updateTableField(id, { grouping }),
        setRowPinning: (id, rowPinning) => updateTableField(id, { rowPinning }),
        setExpandLoading: (id, expandLoading) => updateTableField(id, { expandLoading }),
        register: (id, initState) =>
          set(draft => {
            draft.state[id] = { ...initTableState, ...initState };
            const persisted = draft.persistedColumnState[id];
            if (persisted) {
              draft.state[id].columnOrderMap = persisted.columnOrderMap;
              draft.state[id].columnSizingMap = persisted.columnSizingMap;
            }
          }),
        setExpandCompareKey: (id, expandCompareKey) => updateTableField(id, { expandCompareKey }),
        setLoadingRowCountKey: (id, loadingRowCountKey) => updateTableField(id, { loadingRowCountKey }),
        setDataPropertyForExpandComparison: (id, dataPropertyForExpandComparison) =>
          updateTableField(id, { dataPropertyForExpandComparison }),
        setHighlightedFieldAndValue: (id, field, value) =>
          updateTableField(id, { highlightedField: field, highlightedValue: value }),
        setColumnOrder: (id, columnIdKey, columnOrder) => {
          set(draft => {
            if (!draft.state[id]) return;
            draft.state[id].columnOrderMap[columnIdKey] = columnOrder;
            ensurePersistedColumnState(draft, id);
            draft.persistedColumnState[id].columnOrderMap[columnIdKey] = columnOrder;
          });
        },
        setColumnSizing: (id, columnIdKey, columnSizing) => {
          set(draft => {
            if (!draft.state[id]) return;
            draft.state[id].columnSizingMap[columnIdKey] = columnSizing;
            ensurePersistedColumnState(draft, id);
            draft.persistedColumnState[id].columnSizingMap[columnIdKey] = columnSizing;
          });
        },
        resetColumnState: (id, columnIdKey) => {
          set(draft => {
            if (!draft.state[id]) return;
            delete draft.state[id].columnOrderMap[columnIdKey];
            delete draft.state[id].columnSizingMap[columnIdKey];
            if (draft.persistedColumnState[id]) {
              delete draft.persistedColumnState[id].columnOrderMap[columnIdKey];
              delete draft.persistedColumnState[id].columnSizingMap[columnIdKey];
            }
          });
        },
        unregister: id =>
          set(draft => {
            const existing = draft.state[id];
            if (existing) {
              draft.persistedColumnState[id] = {
                columnOrderMap: existing.columnOrderMap,
                columnSizingMap: existing.columnSizingMap,
              };
            }
            delete draft.state[id];
          }),
      };
    }),
    {
      name: TABLE_COLUMN_SETTINGS_KEY,
      storage: createJSONStorage(() => sessionStorage),
      partialize: state => ({ persistedColumnState: state.persistedColumnState }),
    },
  ),
);

export default useTableStore;
